<template>
  <!-- ProProxyUI licence: the modem's IP changes since the customer bought the proxy (the box's rotation log) -->
  <div class="modal fade" id="licenseIpChangeHistoryModal" tabindex="-1" aria-hidden="true" ref="modalEl">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5">IP Change History</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-1 text-sm">
            {{ props.license ? `${props.license.modem_name || 'Server'} · port ${props.license.proxy_http_port} · ${props.license.customer_email || ''}` : 'Selected licence' }}
          </div>
          <div class="mb-3 text-xs aiph-dim">From the box's rotation log, since the customer bought the proxy. The IP belongs to the modem: every change of it is listed.</div>
          <v-data-table
            :headers="headers"
            :items="history"
            :loading="loading"
            items-per-page="-1"
            hide-default-footer
            class="border bg-white text-black aiph-table"
            style="font-size: 13px;"
          >
            <template #item="{ item }">
              <tr>
                <td>{{ formatDate(item.at) }}</td>
                <td>{{ item.source || '-' }}</td>
                <td>
                  <span class="font-mono">{{ ipText(item) }}</span>
                  <span v-if="item.changed === false && !item.error" class="aiph-same">same IP</span>
                </td>
                <td>{{ durationText(item.seconds) }}</td>
                <td><span v-if="item.error" class="aiph-err">{{ item.error }}</span><span v-else>-</span></td>
              </tr>
            </template>
            <template #no-data>
              <div class="py-5 text-center">{{ failed ? 'The IP history could not be loaded. Try again in a minute.' : 'No IP changes since the purchase.' }}</div>
            </template>
            <template #bottom></template>
          </v-data-table>
          <div class="mt-3 text-sm">{{ history.length }} change{{ history.length === 1 ? '' : 's' }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '@/stores';

const props = defineProps({
  license: {
    type: Object,
    default: null,
  },
});

const history = ref([]);
const loading = ref(false);
const failed = ref(false);

const headers = [
  { title: 'Time', key: 'at', align: 'start' },
  { title: 'Source', key: 'source', align: 'start' },
  { title: 'IP', key: 'new_ip', align: 'start' },
  { title: 'Took', key: 'seconds', align: 'start' },
  { title: 'Error', key: 'error', align: 'start' },
];

// POST /admin/proproxyui/ip-history { license_uuid } -> { history: [{ at, old_ip, new_ip, changed, seconds, source, error }] }, newest first
const load = async () => {
  if (!props.license?.uuid || props.license.modem_type !== 'PROPROXYUI') return;
  loading.value = true;
  failed.value = false;
  try {
    const res = await fetchWrapper.post(`${process.env.VUE_APP_VUE_API_URL}/admin/proproxyui/ip-history`, { license_uuid: props.license.uuid });
    history.value = (res && res.history) || [];
  } catch (e) {
    failed.value = true;
    history.value = [];
    useAlertStore().error(e);
  }
  loading.value = false;
};

const ipText = (item) => {
  if (!item.old_ip && !item.new_ip) return '-';
  if (item.changed === false || item.old_ip === item.new_ip) return item.new_ip || item.old_ip;
  return `${item.old_ip || '-'} → ${item.new_ip || '-'}`;
};

const durationText = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return '-';
  return s < 60 ? `${Math.round(s)} s` : `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

// a different licence: the old list must not show while the new one loads
watch(() => props.license?.uuid, () => {
  history.value = [];
});

const modalEl = ref(null);
onMounted(() => {
  modalEl.value && modalEl.value.addEventListener('shown.bs.modal', load);
});

onBeforeUnmount(() => {
  modalEl.value && modalEl.value.removeEventListener('shown.bs.modal', load);
});
</script>

<style scoped>
.aiph-dim { color: #6b7280; }
.aiph-same { margin-left: 6px; padding: 0 6px; border-radius: 999px; font-size: 11px; background: rgba(127, 127, 127, 0.15); color: #6b7280; }
.aiph-err { color: #dc2626; }
</style>

<style>
body.dark .aiph-dim, body.dark .aiph-same { color: #bcbbc7; }
body.dark .aiph-same { background: #45445e; }
body.dark .aiph-table { background: #34334a !important; color: #ffffff !important; border-color: #45445e !important; }
</style>
